// Настройки одного подключённого номера: какие чаты агент не трогает.
//
// Один компонент на оба канала (MAX и Telegram): правила одинаковые, различаются только
// ручки. Живёт в модалке, а не в карточке: у карточки задача - показать состояние номера,
// и список чатов в ней утопил бы главное.
//
// Почему список чатов подгружаем из мессенджера, а не просим вводить id руками: id чата
// человеку взять неоткуда. Ручной ввод оставлен запасным путём - когда чат в список не
// попал (мессенджер отдаёт только недавние).

import { useState, type FormEvent } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ProhibitIcon, TrashIcon } from '@phosphor-icons/react';
import { ApiError, Button, Input, Label, Skeleton } from '@smena/ui';

export interface IgnoredRule {
  id: string;
  ref: string;
  kind: string;
  note: string | null;
  vendor: boolean;
}

export interface DialogOption {
  chatId: number | string;
  title: string;
  /** Телефон собеседника, если мессенджер его отдаёт: по нему чат узнают наверняка. */
  phone?: string;
  /** @username - у Telegram есть, у MAX нет. */
  username?: string;
}

/** Сколько диалогов показываем за раз. */
const PAGE_SIZE = 15;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Подпись модалки: номер, к которому относятся настройки. */
  subtitle: string;
  queryKey: readonly unknown[];
  fetchRules: () => Promise<{ items: IgnoredRule[] }>;
  addRule: (chatId: string, note?: string) => Promise<unknown>;
  removeRule: (ruleId: string) => Promise<unknown>;
  /** Список диалогов из мессенджера. Может отсутствовать - тогда только ручной ввод. */
  fetchDialogs?: () => Promise<{ items: DialogOption[] }>;
}

export function IgnoredChatsDialog({
  open,
  onOpenChange,
  subtitle,
  queryKey,
  fetchRules,
  addRule,
  removeRule,
  fetchDialogs,
}: Props) {
  const qc = useQueryClient();
  const [manualId, setManualId] = useState('');
  const [search, setSearch] = useState('');
  // Показываем порциями: у активного номера диалогов бывают сотни, а модалка должна
  // открываться, а не превращаться в бесконечную ленту.
  const [visible, setVisible] = useState(PAGE_SIZE);

  const rulesQ = useQuery({ queryKey, queryFn: fetchRules, enabled: open });
  const dialogsQ = useQuery({
    queryKey: [...queryKey, 'dialogs'],
    queryFn: () => fetchDialogs!(),
    // Диалоги тянем только когда модалка открыта: это запрос в мессенджер, а не в нашу БД.
    enabled: open && Boolean(fetchDialogs),
    retry: false,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey });
  const addMut = useMutation({
    mutationFn: ({ chatId, note }: { chatId: string; note?: string }) => addRule(chatId, note),
    onSuccess: () => {
      setManualId('');
      void invalidate();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось добавить чат'),
  });
  const removeMut = useMutation({
    mutationFn: removeRule,
    onSuccess: () => void invalidate(),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось снять правило'),
  });

  const rules = rulesQ.data?.items ?? [];
  const ignoredRefs = new Set(rules.map((r) => r.ref));
  const all = (dialogsQ.data?.items ?? []).filter((d) => !ignoredRefs.has(String(d.chatId)));
  // Ищем по всему, чем чат можно опознать: имя, телефон, username, идентификатор.
  const needle = search.trim().toLowerCase();
  const found = needle
    ? all.filter((d) =>
        [d.title, d.phone, d.username, String(d.chatId)]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(needle)),
      )
    : all;
  const dialogs = found.slice(0, visible);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-ink/40" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[80dvh] w-[min(34rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col gap-3 overflow-y-auto rounded-2xl bg-panel p-4 shadow-pop">
          <div>
            <DialogPrimitive.Title className="text-sm font-semibold">
              Чаты, которые агент не трогает
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="text-xs text-muted">
              {subtitle}. В этих чатах агент не отвечает и не берёт переписку в базу знаний -
              подходит для служебных ботов, коллег и личных диалогов.
            </DialogPrimitive.Description>
          </div>

          <section className="flex flex-col gap-1.5">
            <h3 className="text-[11px] font-bold tracking-wide text-muted uppercase">В игноре</h3>
            {rulesQ.isPending ? <Skeleton className="h-8" /> : null}
            {!rulesQ.isPending && rules.length === 0 ? (
              <p className="text-xs text-muted">Пока ни одного чата - агент обрабатывает все.</p>
            ) : null}
            {rules.map((rule) => (
              <div
                key={rule.id}
                className="flex items-center justify-between gap-2 rounded-md border border-line bg-panel2/40 px-2.5 py-1.5"
              >
                <div className="min-w-0">
                  <div className="truncate text-xs">
                    {rule.note ?? (rule.kind === 'sender' ? `Отправитель ${rule.ref}` : `Чат ${rule.ref}`)}
                  </div>
                  <div className="font-mono text-[11px] text-muted">{rule.ref}</div>
                </div>
                {rule.vendor ? (
                  // Вендорское правило снять из панели нельзя: это служебные боты мессенджера,
                  // общие для всех клиентов. Показываем, чтобы «почему не отвечает» не было загадкой.
                  <span className="shrink-0 text-[11px] text-muted">правило Smena</span>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={removeMut.isPending}
                    onClick={() => removeMut.mutate(rule.id)}
                    aria-label={`Снять игнор с ${rule.ref}`}
                  >
                    <TrashIcon className="size-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </section>

          {fetchDialogs ? (
            <section className="flex flex-col gap-1.5">
              <h3 className="text-[11px] font-bold tracking-wide text-muted uppercase">
                Диалоги номера
              </h3>
              {all.length > PAGE_SIZE || needle ? (
                <Input
                  aria-label="Поиск по диалогам"
                  placeholder="Поиск: имя, телефон или id"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setVisible(PAGE_SIZE);
                  }}
                />
              ) : null}
              {dialogsQ.isPending ? <Skeleton className="h-8" /> : null}
              {dialogsQ.isError ? (
                <p className="text-xs text-muted">
                  Список диалогов недоступен - добавьте чат по идентификатору ниже.
                </p>
              ) : null}
              {!dialogsQ.isPending && !dialogsQ.isError && dialogs.length === 0 ? (
                <p className="text-xs text-muted">Все диалоги уже в списке или их пока нет.</p>
              ) : null}
              {needle && !found.length ? (
                <p className="text-xs text-muted">Ничего не нашлось по «{search.trim()}».</p>
              ) : null}
              {dialogs.map((d) => (
                <div
                  key={String(d.chatId)}
                  className="flex items-center justify-between gap-2 rounded-md border border-line px-2.5 py-1.5"
                >
                  <div className="min-w-0">
                    <div className="truncate text-xs">{d.title}</div>
                    {/* Вторая строка - чтобы чат опознавался наверняка: имя бывает
                        одинаковым у двух гостей, телефон и id - нет. */}
                    <div className="truncate font-mono text-[11px] text-muted">
                      {[d.phone ? `+${String(d.phone).replace(/^\+/, '')}` : null, d.username ? `@${d.username}` : null, String(d.chatId)]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={addMut.isPending}
                    onClick={() => addMut.mutate({ chatId: String(d.chatId), note: d.title })}
                  >
                    <ProhibitIcon className="mr-1 size-3.5" />
                    Не трогать
                  </Button>
                </div>
              ))}
              {found.length > dialogs.length ? (
                <Button
                  size="sm"
                  variant="secondary"
                  className="self-start"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                >
                  Показать ещё ({found.length - dialogs.length})
                </Button>
              ) : null}
            </section>
          ) : null}

          <form
            className="flex flex-col gap-1.5 border-t border-line pt-3"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              if (manualId.trim()) addMut.mutate({ chatId: manualId.trim() });
            }}
          >
            <Label htmlFor="ignored-manual">Добавить по идентификатору чата</Label>
            <div className="flex gap-2">
              <Input
                id="ignored-manual"
                value={manualId}
                onChange={(e) => setManualId(e.target.value)}
                placeholder="например 395556435"
              />
              <Button type="submit" size="sm" disabled={!manualId.trim() || addMut.isPending}>
                Добавить
              </Button>
            </div>
          </form>

          <div className="flex justify-end">
            <Button size="sm" variant="secondary" onClick={() => onOpenChange(false)}>
              Готово
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
